<script lang="ts" module>
  // Badge — the design-system neutral status-badge primitive
  // (wiki/feature-design-system.md §Component primitives, AC4). A small pill
  // carrying a short status word, tinted by a SEMANTIC tone (never the practice
  // accent). Distinct from `StatusPill`, which models the AI-Review lifecycle
  // set (Reviewed / Pending / Failed / Recording) with a leading dot — Badge is
  // the generic data-row badge the admin tables use (Active / Inactive /
  // Waiting / Admitted). No bespoke per-page `.badge-*` chrome.
  //   • neutral  — inert / inactive (muted ink, hairline)
  //   • positive — active / admitted (leaf)
  //   • negative — failed / blocked (recording-red)
  //   • info     — waiting / pending (cobalt)
  export type BadgeTone = 'neutral' | 'positive' | 'negative' | 'info';
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';

  interface Props {
    /** Semantic tone — drives the tint. */
    tone: BadgeTone;
    /**
     * Accessible label. Defaults to the visible text; override when the
     * announced status should differ from the glyph/label shown.
     */
    ariaLabel?: string;
    class?: string;
    /** The badge label content. */
    children: Snippet;
  }

  let { tone, ariaLabel, class: klass = '', children }: Props = $props();
</script>

<span class="vd-badge vd-badge--{tone} {klass}" aria-label={ariaLabel}>
  {@render children()}
</span>

<style lang="scss">
  .vd-badge {
    display: inline-block;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    line-height: 1.4;
    text-align: center;
    /* Pinning a badge to one line clips it against the neighbour beside it, and
       an unreadable badge is worse than a wide one. Surfaces give a badge its
       line (see /pricing's pack grid); this is the degradation when one cannot
       — balanced, centred, still a pill. */
    max-width: 100%;
    text-wrap: balance;
  }

  // Semantic tones — theme-independent, NOT the practice accent.
  .vd-badge--neutral {
    background: color-mix(in srgb, var(--ink) 6%, transparent);
    color: var(--ink-mute);
    border: 1px solid var(--line);
  }

  .vd-badge--positive {
    background: color-mix(in srgb, var(--color-leaf) 18%, transparent);
    color: var(--color-leaf-text);
    border: 1px solid color-mix(in srgb, var(--color-leaf) 40%, transparent);
  }

  .vd-badge--negative {
    background: color-mix(in srgb, var(--color-recording) 14%, transparent);
    color: var(--color-recording-text);
    border: 1px solid color-mix(in srgb, var(--color-recording) 40%, transparent);
  }

  .vd-badge--info {
    background: color-mix(in srgb, var(--color-cobalt) 14%, transparent);
    color: var(--color-cobalt-text);
    border: 1px solid color-mix(in srgb, var(--color-cobalt) 40%, transparent);
  }
</style>
